// FILE: src/hooks/useMediaQuery.js
//
// Lightweight hook that tracks whether a CSS media query matches.
// Used ONLY for behavioural changes (e.g. switching the navigator to a
// drawer on mobile).  Layout-only responsiveness is handled by CSS Modules
// with @media queries.
//
// Usage:
//   const isMobile = useMediaQuery('(max-width: 600px)');

import { useState, useEffect } from 'react';

export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);

    // Modern browsers
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
    } else {
      // Safari < 14 fallback
      mql.addListener(handler);
    }

    // Sync on mount in case SSR initial value was wrong
    setMatches(mql.matches);

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler);
      } else {
        mql.removeListener(handler);
      }
    };
  }, [query]);

  return matches;
}

// ── Standard breakpoints ──────────────────────────────────────────────────────
// These string constants are exported for use in both JS (useMediaQuery) and
// CSS Module files so the breakpoints stay consistent.

export const BREAKPOINTS = {
  mobile:  600,
  tablet:  900,
};

export const MQ = {
  mobile:    `(max-width: ${BREAKPOINTS.mobile}px)`,
  tablet:    `(max-width: ${BREAKPOINTS.tablet}px)`,
  notMobile: `(min-width: ${BREAKPOINTS.mobile + 1}px)`,
  desktop:   `(min-width: ${BREAKPOINTS.tablet + 1}px)`,
};
